<template>
  <div v-if="showProgress" class="progress-container">
    <el-card class="progress-card">
      <div class="progress-header">
        <h3>Enviando visitas a Simpliroute</h3>
        <el-button 
          v-if="status !== 'completed' && status !== 'failed'" 
          @click="cancelProgress" 
          type="text" 
          size="small">
          Cancelar
        </el-button>
      </div>
      
      <div class="progress-content">
        <el-progress 
          :percentage="completionPercentage" 
          :status="progressStatus"
          :stroke-width="8"
          :text-inside="true">
        </el-progress>
        
        <div class="progress-details">
          <div class="progress-info">
            <span class="status-text">{{ statusText }}</span>
            <span class="percentage-text">{{ completionPercentage }}%</span>
          </div>
          
          <div v-if="total > 0" class="progress-stats">
            <span>{{ totalCreated }} de {{ total }} visitas procesadas</span>
          </div>
          
          <div v-if="lastUpdate" class="progress-timestamp">
            Última actualización: {{ formatTime(lastUpdate) }}
          </div>
        </div>
        
        <div v-if="errors && errors.length > 0" class="progress-errors">
          <el-alert
            title="Errores encontrados"
            type="warning"
            :closable="false"
            show-icon>
            <ul>
              <li v-for="(error, index) in errors" :key="index">
                {{ error }}
              </li>
            </ul>
          </el-alert>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { apiRequest } from '../../../../backend/vue-axios/axios';
import { getRumDuration, startRumTiming, trackBusinessFlow } from '@/observability/datadogRum';

export default {
  name: 'ProgressBar',
  props: {
    batchId: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      status: 'pending',
      completionPercentage: 0,
      total: 0,
      totalCreated: 0,
      timestamp: null,
      errors: null,
      pollingInterval: null,
      lastUpdate: null,
      pollingStartTime: null,
      maxPollingTime: 10 * 60 * 1000, // 10 minutos máximo
      pollStartedAt: null,
    };
  },
  computed: {
    showProgress() {
      return this.show && this.batchId;
    },
    progressStatus() {
      switch (this.status) {
        case 'completed':
          return 'success';
        case 'failed':
          return 'exception';
        case 'processing':
        case 'pending':
        default:
          return null; // null es válido para el-progress
      }
    },
    statusText() {
      switch (this.status) {
        case 'pending':
          return 'Esperando que el servidor procese el batch...';
        case 'processing':
          return 'Procesando visitas...';
        case 'completed':
          return '¡Proceso completado!';
        case 'failed':
          return 'Error en el proceso';
        default:
          return 'Procesando...';
      }
    },
  },
  watch: {
    show(newVal) {
      if (newVal && this.batchId) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
    batchId(newVal) {
      if (newVal && this.show) {
        this.startPolling();
      }
    },
  },
  mounted() {
    if (this.show && this.batchId) {
      this.startPolling();
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    startPolling() {
      this.stopPolling(); // Asegurar que no hay polling activo
      this.pollingStartTime = new Date();
      this.pollStartedAt = startRumTiming();
      trackBusinessFlow({
        module: 'planning',
        flow: 'simpliroute_visits',
        action: 'progress_tracking_started',
        status: 'success',
        context: {
          batch_id: this.batchId,
        },
      });
      this.pollStatus();
      this.pollingInterval = setInterval(() => {
        // Verificar timeout máximo
        if (this.pollingStartTime && (new Date() - this.pollingStartTime) > this.maxPollingTime) {
          trackBusinessFlow({
            module: 'planning',
            flow: 'simpliroute_visits',
            action: 'progress_tracking_completed',
            status: 'timeout',
            durationMs: getRumDuration(this.pollStartedAt),
            context: {
              batch_id: this.batchId,
              total: this.total,
              total_created: this.totalCreated,
            },
          });
          console.log('Timeout máximo alcanzado, deteniendo polling');
          this.status = 'failed';
          this.stopPolling();
          this.$notify({
            type: 'warning',
            title: 'Timeout',
            message: 'El proceso está tomando más tiempo del esperado. Por favor, verifica el estado manualmente.',
          });
          return;
        }
        this.pollStatus();
      }, 2000); // Polling cada 2 segundos
    },
    
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    
    async pollStatus() {
      if (!this.batchId) {
        return;
      }
      
      try {
        console.log('Consultando estado del batch:', this.batchId);
        const response = await apiRequest({
          module: 'toolkit',
          service: 'visitCreationProgress',
          pathparam: { batchId: this.batchId },
        });
        
        const data = response.data;
        console.log('Respuesta exitosa del batch:', data);
        this.updateProgress(data);
        
        // Solo detener el polling cuando el completionPercentage sea 100
        if (data.completionPercentage >= 100) {
          console.log('Batch completado al 100%, deteniendo polling y ejecutando handleCompletion');
          this.stopPolling();
          this.handleCompletion(data);
        }
        
      } catch (error) {
        console.error('Error al consultar el estado del batch:', error);
        
        // Si es un 404, el batch aún no está listo, continuar como pendiente
        if (error.response && error.response.status === 404) {
          console.log('Batch aún no está listo (404), continuando como pendiente...');
          this.status = 'pending';
          this.completionPercentage = 0;
          // No detener el polling, continuar consultando
          return;
        }
        
        // Para otros errores, marcar como fallido
        trackBusinessFlow({
          module: 'planning',
          flow: 'simpliroute_visits',
          action: 'progress_tracking_completed',
          status: 'error',
          durationMs: getRumDuration(this.pollStartedAt),
          context: {
            batch_id: this.batchId,
            error_status: error.response ? error.response.status : null,
          },
        });
        this.status = 'failed';
        this.stopPolling();
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'Error al consultar el estado del proceso',
        });
      }
    },
    
    updateProgress(data) {
      this.status = data.status;
      this.completionPercentage = data.completionPercentage || 0;
      this.total = data.total || 0;
      this.totalCreated = data.totalCreated || 0;
      this.timestamp = data.timestamp;
      this.errors = data.errors;
      this.lastUpdate = new Date();
    },
    
    handleCompletion(data) {
      console.log('handleCompletion llamado con data:', data);
      if (data.status === 'completed') {
        trackBusinessFlow({
          module: 'planning',
          flow: 'simpliroute_visits',
          action: 'progress_tracking_completed',
          status: data.errors && data.errors.length > 0 ? 'partial_success' : 'success',
          durationMs: getRumDuration(this.pollStartedAt),
          context: {
            batch_id: this.batchId,
            total: data.total,
            total_created: data.totalCreated || 0,
            errors_count: data.errors ? data.errors.length : 0,
          },
        });
        console.log('Emitiendo evento completed con data:', data);
        
        // Determinar el tipo de notificación basado en si hay errores
        if (data.errors && data.errors.length > 0) {
          this.$notify({
            type: 'warning',
            title: 'Proceso completado con errores',
            message: `Se procesaron ${data.totalCreated || 0} de ${data.total} visitas. ${data.errors.length} errores encontrados.`,
          });
        } else {
          this.$notify({
            type: 'success',
            title: '¡Éxito!',
            message: `Se procesaron ${data.totalCreated} de ${data.total} visitas correctamente`,
          });
        }
        
        this.$emit('completed', data);
      } else if (data.status === 'failed') {
        trackBusinessFlow({
          module: 'planning',
          flow: 'simpliroute_visits',
          action: 'progress_tracking_completed',
          status: 'failed',
          durationMs: getRumDuration(this.pollStartedAt),
          context: {
            batch_id: this.batchId,
            total: data.total,
            total_created: data.totalCreated || 0,
            errors_count: data.errors ? data.errors.length : 0,
          },
        });
        console.log('Emitiendo evento failed con data:', data);
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'El proceso falló al procesar las visitas',
        });
        this.$emit('failed', data);
      }
    },
    
    cancelProgress() {
      trackBusinessFlow({
        module: 'planning',
        flow: 'simpliroute_visits',
        action: 'progress_tracking_cancelled',
        status: 'cancelled',
        durationMs: getRumDuration(this.pollStartedAt),
        context: {
          batch_id: this.batchId,
          total: this.total,
          total_created: this.totalCreated,
        },
      });
      this.stopPolling();
      this.$emit('cancelled');
    },
    
    formatTime(date) {
      return new Intl.DateTimeFormat('es-CL', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    },
  },
};
</script>

<style lang="scss" scoped>
.progress-container {
  margin: 20px 0;
}

.progress-card {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    color: #303133;
    font-size: 16px;
    font-weight: 600;
  }
}

.progress-content {
  .progress-details {
    margin-top: 15px;
  }
  
  .progress-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    
    .status-text {
      color: #606266;
      font-size: 14px;
      font-weight: 500;
    }
    
    .percentage-text {
      color: #409eff;
      font-size: 14px;
      font-weight: 600;
    }
  }
  
  .progress-stats {
    color: #909399;
    font-size: 12px;
    margin-bottom: 5px;
  }
  
  .progress-timestamp {
    color: #c0c4cc;
    font-size: 11px;
  }
  
  .progress-errors {
    margin-top: 15px;
    
    ul {
      margin: 5px 0 0 0;
      padding-left: 20px;
      
      li {
        margin-bottom: 5px;
        font-size: 12px;
      }
    }
  }
}

:deep(.el-progress-bar__outer) {
  background-color: #f5f7fa;
}

:deep(.el-progress-bar__inner) {
  transition: width 0.3s ease;
}
</style>
